import { action } from '@ember/object';
import { next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import ArtifactsEntryPoint from '@databox/ember-integrations/lib/components/Ai/Artifacts/ArtifactsEntryPoint';

import type { ReactIntegrations, Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { encodeAiChatPrompt } from 'webapp/utils/ai-chat-payload';
import { getShadowDomMode } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';

const NEW_ARTIFACT_PROMPT = 'Help me create a new artifact';

interface GenieArtifactsSignature {
	Element: HTMLDivElement;
}

export default class GenieArtifactsComponent extends Component<GenieArtifactsSignature> {
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('react') declare reactService: Service.React;

	reactElement: ReactIntegrations.Root = null;

	@action
	setupView(node: HTMLElement) {
		this.reactElement = this.reactService.create(node as HTMLDivElement);

		schedule('afterRender', () => {
			next(() => {
				this.renderArtifacts();
			});
		});

		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.renderArtifacts);
		this.eventManagerService.on(AppEvent.ACCESS_TOKEN_REFRESHED, this, this.renderArtifacts);
	}

	@action
	teardownView() {
		this.reactService.unmount(this.reactElement);
		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
		this.eventManagerService.off(AppEvent.ACCESS_TOKEN_REFRESHED, this);
	}

	@action
	navigateToNewArtifact() {
		// Land on the welcome page with the prompt prefilled and ready to send — not sent.
		this.navigationService.transitionToRoute('genie', {
			queryParams: { chat: encodeAiChatPrompt(NEW_ARTIFACT_PROMPT) },
		});
	}

	@action
	openArtifactInChat(
		artifact: { id: string; threadId: string },
		run?: { routineId: string; runId: string },
	) {
		// A routine's document belongs to a run, so it opens inside the routine — the run's thread,
		// under the routine breadcrumbs — rather than as a bare conversation.
		if (run) {
			this.navigationService.transitionToRoute('routines.run', run.routineId, run.runId);

			return;
		}

		// Land on the artifact itself, not just its conversation — the gallery is the main entry
		// point for the deep link.
		this.navigationService.transitionToRoute(
			'genie.chat.thread.artifact',
			artifact.threadId,
			artifact.id,
		);
	}

	private renderArtifacts() {
		if (!this.reactElement) {
			return;
		}

		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		const props = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			accessToken: accessToken!,
			dataToken: dataToken!,
			spaceId: this.accountService.id,
			user: {
				id: Number(this.currentUserService.id),
				name: this.currentUserService.displayName,
			},
			trackingOrigin: this.aiChatService.trackingOrigin,
			showToast: this.aiChatService.showToast,
			onCreateArtifact: this.navigateToNewArtifact,
			onOpenInChat: this.openArtifactInChat,
			onUpgrade: this.aiChatService.onUpgrade,
			onAddCredits: this.aiChatService.onAddCredits,
		};

		this.reactService.render(
			this.reactElement,
			ArtifactsEntryPoint(props),
			getShadowDomMode(this.accountService?.email),
		);
	}

	<template>
		<div
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
			id="genie-artifacts-container"
			...attributes
		/>
	</template>
}
